import * as z from "zod";
import { reconcileWorkflowConnectorUsagesOnMove } from "../connectorUsages";
import { findUniqueName } from "../findUniqueName";
import { zql } from "../generatedDrizzleSchema";
import { uniqueWorkflowName } from "../workflowName";
import { assertCanChangeLiveness } from "./livenessGate";
import { mutator } from "./mutator";

export const moveWorkflowSchema = z.object({
  workflowId: z.string(),
  spaceId: z.string(),
});

export const moveWorkflow = mutator({
  schema: moveWorkflowSchema,
  resolve: {
    workflow: { kind: "workflow", from: "workflowId" },
    targetSpace: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.workflow.spaceId,
    },
    {
      permission: "space:write",
      scopeId: ({ args }) => args.targetSpace.id,
    },
  ],
  fn: async ({ tx, ctx, args: { workflow, targetSpace } }) => {
    let name = workflow.name;
    if (workflow.spaceId !== targetSpace.id) {
      await assertCanChangeLiveness(tx, ctx, workflow);

      await reconcileWorkflowConnectorUsagesOnMove(
        tx,
        workflow.id,
        targetSpace
      );

      const targetWorkflows = await tx.run(
        zql.workflows
          .where("spaceId", targetSpace.id)
          .where("deletedAt", "IS", null)
      );
      name = uniqueWorkflowName(
        workflow.name,
        targetWorkflows.map((w) => w.name)
      );

      // space:push_live is granted per space, so a move must not carry liveness
      // over: the live version becomes a draft owned by the mover.
      const [branches, [sourceSpace]] = await Promise.all([
        tx.run(zql.workflowBranches.where("workflowId", workflow.id)),
        tx.run(zql.spaces.where("id", workflow.spaceId)),
      ]);
      const branchIds = branches.map((branch) => branch.id);
      const volumes =
        branchIds.length > 0
          ? await tx.run(
              zql.spaceVolumes
                .where("spaceId", workflow.spaceId)
                .where("branchId", "IN", branchIds)
            )
          : [];
      for (const volume of volumes) {
        await tx.mutate.spaceVolumes.delete({ id: volume.id });
      }
      const mainBranch = branches.find((b) => b.name === "main");
      if (mainBranch) {
        await tx.mutate.workflowBranches.update({
          id: mainBranch.id,
          name: findUniqueName(
            `Live from ${sourceSpace!.name}`,
            new Set(branches.map((b) => b.name))
          ),
          userId: ctx.userId,
        });
      }
    }

    await tx.mutate.workflows.update({
      id: workflow.id,
      spaceId: targetSpace.id,
      name,
    });
  },
});
